import { useRef, useState } from "react";
import { Canvas, RootState } from "@react-three/fiber";
import { useGSAP } from "@gsap/react";
import { gsap } from "@/utils/gsap";
import { Float, PerspectiveCamera } from "@react-three/drei";
import Duck from "@/components/common/3D/Duck";
import { AspectRatio } from "@/components/common/3D/AspectRatio";

import style from "./HeroAbout.module.scss";
import { BreakPointProps, useResponsive3D } from "@/utils/useResponsive3D";
import SceneProps from "@/components/common/3D/SceneProps";
import { degToRad } from "three/src/math/MathUtils.js";

const AboutDucks = ({ trigger }) => {
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const contentRef = useRef(null);
  const viewportRef = useRef(null);

  const handleSetProps = (rootState: RootState) => {
    viewportRef.current = rootState.viewport;
  };

  const duck01 = useRef(null);
  const duck02 = useRef(null);
  const duck03 = useRef(null);
  const duck04 = useRef(null);

  const [isReady, setIsReady] = useState(false);

  const timelineRef = useRef(null);

  const breakPoints: BreakPointProps[] = [
    {
      breakpoint: [300, 1000],
      props: {
        position: [0, 0, 0],
        rotation: [0, 15, 0],
        offset: 1000,
      },
    },
    {
      breakpoint: [1001, 1900],
      props: {
        position: [8, -1, 0],
        rotation: [0, 15, 0],
        offset: 600,
      },
    },
    {
      breakpoint: [1901, 2500],
      props: {
        position: [10, -1, 8],
        rotation: [0, 15, 0],
        offset: 600,
      },
    },
  ];

  const { breakProps } = useResponsive3D({ breakPoints: breakPoints });

  useGSAP(
    () => {
      if (!isReady) return;

      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current.scrollTrigger?.kill();
      }

      const contentSize = 54;
      const waveForce = 2.75;
      const waveDecrement = 0.5;

      timelineRef.current = gsap.timeline({
        scrollTrigger: {
          trigger: sceneRef.current,
          scrub: true,
          start: `top top`,
          endTrigger: `.${trigger}`,
          end: `bottom+=${breakProps?.offset}px bottom`,
          pin: true,
          pinSpacing: false,
          invalidateOnRefresh: true,
          onUpdate: (e) => {
            gsap.to(duck01.current.rotation, {
              z:
                Math.sin(Math.PI * e.progress * -waveForce) *
                Math.cos(Math.PI * e.progress * -waveForce),
              duration: 1,
            });
            gsap.to(duck02.current.rotation, {
              z:
                Math.sin(Math.PI * e.progress * (waveForce - waveDecrement)) *
                Math.cos(Math.PI * e.progress * (waveForce - waveDecrement)),
              duration: 1,
            });
            gsap.to(duck03.current.rotation, {
              z:
                Math.sin(
                  Math.PI * e.progress * -(waveForce - waveDecrement * 2)
                ) *
                Math.cos(
                  Math.PI * e.progress * -(waveForce - waveDecrement * 2)
                ),
              duration: 1,
            });
            gsap.to(duck04.current.rotation, {
              z:
                Math.sin(
                  Math.PI * e.progress * (waveForce - waveDecrement * 3)
                ) *
                Math.cos(
                  Math.PI * e.progress * (waveForce - waveDecrement * 3)
                ),
              duration: 1,
            });
          },
        },
      });

      timelineRef.current.to(contentRef.current.position, {
        x: -contentSize - viewportRef.current.width - breakProps?.position[0],
        ease: "none",
      });
    },
    {
      dependencies: [isReady, breakProps],
      revertOnUpdate: true,
    }
  );

  return (
    <div className={style.canvas} ref={sceneRef}>
      <Canvas
        gl={{
          stencil: false,
          autoClear: true,
          alpha: true,
          antialias: true,
          precision: "mediump",
          powerPreference: "high-performance",
        }}
        style={{
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
        frameloop="always"
        onCreated={() => {
          setIsReady(true);
        }}
      >
        <PerspectiveCamera
          position={[0, 0, 30]}
          rotation={[0, 0, 0]}
          fov={30}
          makeDefault
          ref={cameraRef}
        />
        <SceneProps handleSetProps={handleSetProps} />
        <AspectRatio camera={cameraRef.current} />
        <group
          ref={contentRef}
          position={[
            breakProps?.position[0],
            breakProps?.position[1],
            breakProps?.position[2],
          ]}
          rotation={[
            degToRad(breakProps?.rotation[0]),
            degToRad(breakProps?.rotation[1]),
            degToRad(breakProps?.rotation[2]),
          ]}
        >
          <group
            ref={duck01}
            position={[0, -3, 0]}
            rotation={[degToRad(0), degToRad(-60), degToRad(0)]}
          >
            <Float speed={2}>
              <Duck
                scale={10}
                cursorAnimations
                cursorAngleLimit={15}
                cursorSmoothFactor={0.45}
              />
            </Float>
          </group>
          <group
            ref={duck02}
            position={[10, -3, 0]}
            rotation={[degToRad(0), degToRad(-60), degToRad(0)]}
          >
            <Float speed={2}>
              <Duck
                scale={9}
                cursorAnimations
                cursorAngleLimit={15}
                cursorSmoothFactor={0.75}
              />
            </Float>
          </group>
          <group
            ref={duck03}
            position={[20, -3, 0]}
            rotation={[degToRad(0), degToRad(-60), degToRad(0)]}
          >
            <Float speed={2}>
              <Duck
                scale={8}
                cursorAnimations
                cursorAngleLimit={15}
                cursorSmoothFactor={0.25}
              />
            </Float>
          </group>
          <group
            ref={duck04}
            position={[30, -3, 0]}
            rotation={[degToRad(0), degToRad(-60), degToRad(0)]}
          >
            <Float speed={2}>
              <Duck scale={7} cursorAnimations cursorAngleLimit={15} />
            </Float>
          </group>
        </group>
      </Canvas>
    </div>
  );
};

export default AboutDucks;

AboutDucks.displayName = "AboutDucks";
